import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Save, X, Plus, Trash2, Sparkles, Eye, ArrowLeft, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScenarioEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Safe Nightlife',
    difficulty: 'medium',
    duration: '8 min',
    thumbnail: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800',
    learning_objectives: [''],
    entry_node_id: 'node_1',
    nodes: [
      {
        node_id: 'node_1',
        content_type: 'story',
        content_text: '',
        content_image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800',
        choices: [
          { id: 'c1', text: '', next_node_id: 'node_2', xp_delta: 25 }
        ]
      }
    ]
  });

  const categories = [
    'Safe Nightlife', 'Substance Use', 'Peer Support',
    'Mental Health', 'Communication', 'Harm Reduction'
  ];

  useEffect(() => {
    if (isEditMode) {
      fetchScenario();
    }
  }, [id]);

  const fetchScenario = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/scenarios/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const scenario = response.data;
      setFormData({
        title: scenario.title,
        description: scenario.description,
        category: scenario.category,
        difficulty: scenario.difficulty || 'medium',
        duration: scenario.duration,
        thumbnail: scenario.thumbnail,
        learning_objectives: scenario.learning_objectives || [''],
        entry_node_id: scenario.entry_node_id || 'node_1',
        nodes: scenario.nodes || []
      });
    } catch (error) {
      console.error('Failed to fetch scenario:', error);
      toast.error('Failed to load scenario');
      navigate('/admin/scenarios');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (status = 'draft') => {
    // Validation
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (formData.nodes.length === 0) {
      toast.error('At least one node is required');
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('adminToken');
      const payload = {
        ...formData,
        status,
        learning_objectives: formData.learning_objectives.filter(obj => obj.trim())
      };

      if (isEditMode) {
        await axios.put(`${API}/admin/scenarios/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Scenario updated successfully');
      } else {
        await axios.post(`${API}/admin/scenarios`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Scenario created successfully');
      }

      navigate('/admin/scenarios');
    } catch (error) {
      console.error('Failed to save scenario:', error);
      toast.error(error.response?.data?.detail || 'Failed to save scenario');
    } finally {
      setSaving(false);
    }
  };

  const addNode = () => {
    const newNodeId = `node_${formData.nodes.length + 1}`;
    setFormData({
      ...formData,
      nodes: [
        ...formData.nodes,
        {
          node_id: newNodeId,
          content_type: 'consequence',
          content_text: '',
          content_image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800',
          choices: [
            { id: `c${formData.nodes.length + 1}_1`, text: '', next_node_id: '', xp_delta: 25 }
          ]
        }
      ]
    });
  };

  const addDebriefNode = () => {
    const newNodeId = `node_end_${formData.nodes.length + 1}`;
    setFormData({
      ...formData,
      nodes: [
        ...formData.nodes,
        {
          node_id: newNodeId,
          content_type: 'debrief',
          content_text: 'Well done completing this scenario!',
          debrief: {
            harm_reduction_tips: [''],
            reflection_questions: ['']
          }
        }
      ]
    });
  };

  const updateNode = (index, field, value) => {
    const updatedNodes = [...formData.nodes];
    updatedNodes[index] = { ...updatedNodes[index], [field]: value };
    setFormData({ ...formData, nodes: updatedNodes });
  };

  const addChoice = (nodeIndex) => {
    const updatedNodes = [...formData.nodes];
    const choiceId = `c${nodeIndex}_${(updatedNodes[nodeIndex].choices?.length || 0) + 1}`;
    
    updatedNodes[nodeIndex].choices = [
      ...(updatedNodes[nodeIndex].choices || []),
      { id: choiceId, text: '', next_node_id: '', xp_delta: 25 }
    ];
    
    setFormData({ ...formData, nodes: updatedNodes });
  };

  const updateChoice = (nodeIndex, choiceIndex, field, value) => {
    const updatedNodes = [...formData.nodes];
    updatedNodes[nodeIndex].choices[choiceIndex][field] = value;
    setFormData({ ...formData, nodes: updatedNodes });
  };

  const removeChoice = (nodeIndex, choiceIndex) => {
    const updatedNodes = [...formData.nodes];
    updatedNodes[nodeIndex].choices.splice(choiceIndex, 1);
    setFormData({ ...formData, nodes: updatedNodes });
  };

  const removeNode = (index) => {
    if (formData.nodes.length === 1) {
      toast.error('Cannot remove the only node');
      return;
    }
    const updatedNodes = formData.nodes.filter((_, i) => i !== index);
    setFormData({ ...formData, nodes: updatedNodes });
  };

  const addLearningObjective = () => {
    setFormData({
      ...formData,
      learning_objectives: [...formData.learning_objectives, '']
    });
  };

  const updateLearningObjective = (index, value) => {
    const updated = [...formData.learning_objectives];
    updated[index] = value;
    setFormData({ ...formData, learning_objectives: updated });
  };

  const removeLearningObjective = (index) => {
    const updated = formData.learning_objectives.filter((_, i) => i !== index);
    setFormData({ ...formData, learning_objectives: updated });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-12 h-12 text-ssdp-teal animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/admin/scenarios')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Scenarios
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Edit Scenario' : 'Create New Scenario'}
          </h1>
        </div>
        
        <div className="flex gap-3">
          <Button
            onClick={() => handleSave('draft')}
            disabled={saving}
            variant="outline"
            className="border-2 border-gray-300"
          >
            {saving ? 'Saving...' : 'Save as Draft'}
          </Button>
          <Button
            onClick={() => handleSave('published')}
            disabled={saving}
            className="bg-gradient-to-r from-ssdp-teal to-ssdp-purple text-white"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Publishing...' : 'Save & Publish'}
          </Button>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            Basic Information
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
                placeholder="e.g., The Party Decision"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
                rows="3"
                placeholder="Brief description of the scenario..."
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Difficulty
                </label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Duration
                </label>
                <input
                  type="text"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
                  placeholder="e.g., 8 min"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Thumbnail URL
              </label>
              <input
                type="url"
                value={formData.thumbnail}
                onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
                placeholder="https://..."
              />
            </div>
          </div>
        </div>

        {/* Learning Objectives */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">
              Learning Objectives
            </h3>
            <Button
              onClick={addLearningObjective}
              variant="outline"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Objective
            </Button>
          </div>

          <div className="space-y-3">
            {formData.learning_objectives.map((obj, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={obj}
                  onChange={(e) => updateLearningObjective(index, e.target.value)}
                  className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
                  placeholder="e.g., Understand peer pressure dynamics"
                />
                {formData.learning_objectives.length > 1 && (
                  <button
                    onClick={() => removeLearningObjective(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Nodes/Story Structure */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900">
              Story Nodes ({formData.nodes.length})
            </h3>
            <div className="flex gap-2">
              <Button onClick={addNode} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-1" />
                Add Node
              </Button>
              <Button onClick={addDebriefNode} variant="outline" size="sm" className="border-green-300">
                <Plus className="w-4 h-4 mr-1" />
                Add Debrief
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            {formData.nodes.map((node, nodeIndex) => (
              <div
                key={node.node_id}
                className="border-2 border-gray-200 rounded-xl p-5 relative"
              >
                {/* Node Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm bg-indigo-100 text-indigo-700 px-3 py-1 rounded-lg">
                      {node.node_id}
                    </span>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      node.content_type === 'story' ? 'bg-blue-100 text-blue-700' :
                      node.content_type === 'consequence' ? 'bg-purple-100 text-purple-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {node.content_type}
                    </span>
                  </div>
                  <button
                    onClick={() => removeNode(nodeIndex)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Node Content */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Content Text
                    </label>
                    <textarea
                      value={node.content_text}
                      onChange={(e) => updateNode(nodeIndex, 'content_text', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
                      rows="3"
                      placeholder="The story content..."
                    />
                  </div>

                  {node.content_type !== 'debrief' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Image URL
                      </label>
                      <input
                        type="url"
                        value={node.content_image || ''}
                        onChange={(e) => updateNode(nodeIndex, 'content_image', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none"
                        placeholder="https://..."
                      />
                    </div>
                  )}

                  {/* Choices (for story/consequence nodes) */}
                  {node.content_type !== 'debrief' && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-semibold text-gray-700">
                          Choices
                        </label>
                        <Button
                          onClick={() => addChoice(nodeIndex)}
                          variant="ghost"
                          size="sm"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Choice
                        </Button>
                      </div>

                      <div className="space-y-3">
                        {node.choices?.map((choice, choiceIndex) => (
                          <div key={choice.id} className="flex gap-2 items-start bg-gray-50 p-3 rounded-lg">
                            <div className="flex-1 space-y-2">
                              <input
                                type="text"
                                value={choice.text}
                                onChange={(e) => updateChoice(nodeIndex, choiceIndex, 'text', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-ssdp-teal focus:outline-none text-sm"
                                placeholder="Choice text..."
                              />
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={choice.next_node_id}
                                  onChange={(e) => updateChoice(nodeIndex, choiceIndex, 'next_node_id', e.target.value)}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:border-ssdp-teal focus:outline-none text-sm font-mono"
                                  placeholder="Next node ID (e.g., node_2)"
                                />
                                <input
                                  type="number"
                                  value={choice.xp_delta}
                                  onChange={(e) => updateChoice(nodeIndex, choiceIndex, 'xp_delta', parseInt(e.target.value))}
                                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:border-ssdp-teal focus:outline-none text-sm"
                                  placeholder="XP"
                                />
                              </div>
                            </div>
                            <button
                              onClick={() => removeChoice(nodeIndex, choiceIndex)}
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Debrief Content */}
                  {node.content_type === 'debrief' && node.debrief && (
                    <div className="space-y-4 border-t-2 border-green-200 pt-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Harm Reduction Tips
                        </label>
                        <textarea
                          value={node.debrief.harm_reduction_tips?.join('\n') || ''}
                          onChange={(e) => {
                            const tips = e.target.value.split('\n');
                            updateNode(nodeIndex, 'debrief', {
                              ...node.debrief,
                              harm_reduction_tips: tips
                            });
                          }}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none text-sm"
                          rows="4"
                          placeholder="One tip per line..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Reflection Questions
                        </label>
                        <textarea
                          value={node.debrief.reflection_questions?.join('\n') || ''}
                          onChange={(e) => {
                            const questions = e.target.value.split('\n');
                            updateNode(nodeIndex, 'debrief', {
                              ...node.debrief,
                              reflection_questions: questions
                            });
                          }}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-ssdp-teal focus:outline-none text-sm"
                          rows="3"
                          placeholder="One question per line..."
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Save Footer */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            onClick={() => navigate('/admin/scenarios')}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleSave('draft')}
            disabled={saving}
            variant="outline"
          >
            Save as Draft
          </Button>
          <Button
            onClick={() => handleSave('published')}
            disabled={saving}
            className="bg-gradient-to-r from-ssdp-teal to-ssdp-purple text-white"
          >
            <Save className="w-5 h-5 mr-2" />
            {isEditMode ? 'Update & Publish' : 'Create & Publish'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ScenarioEditor;
